'use server'

import { apiRequest, ApiResponse } from '../request'

const BASE = 'financial/digital-account'

// ============ SALDOS ============
export interface BalanceOverview {
    availableCents: number // saldo disponível atual
    confirmedCents: number // confirmado aguardando liberação
    pendingCents: number // cobranças em aberto (a receber)
    overdueCents: number // cobranças vencidas
}

export async function getBalanceOverview(): Promise<ApiResponse<BalanceOverview>> {
    return apiRequest<BalanceOverview>(`${BASE}/balance-overview`, {}, 'get')
}

// ============ EXTRATO ============
export interface ExtractItem {
    id: number
    type: string                // nosso LedgerEntryType
    asaasType: string | null    // tipo nativo do Asaas (do raw)
    amount: number              // centavos (pode ser negativo)
    balanceAfter: number | null // centavos
    occurredAt: string
    description: string | null
    providerEventId: string
    billingId: number | null
    transferId: number | null
}

export interface ExtractResult {
    items: ExtractItem[]
    total: number
    page: number
    limit: number
    pages: number
}

export async function getExtract(params?: {
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
}): Promise<ApiResponse<ExtractResult>> {
    return apiRequest<ExtractResult>(`${BASE}/extract`, { params: params || {} }, 'get')
}

export async function syncExtract(data?: {
    startDate?: string
    endDate?: string
    maxPages?: number
}): Promise<ApiResponse<{ imported: number; updated: number; total: number }>> {
    return apiRequest<{ imported: number; updated: number; total: number }>(
        `${BASE}/extract/sync`,
        { data: data || {} },
        'post',
    )
}

// ============ CHAVES PIX ============
export interface PixKey {
    id: string
    key: string
    type: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP'
    status: string
}

export async function listPixKeys(): Promise<ApiResponse<{ items: PixKey[]; raw: any }>> {
    return apiRequest<{ items: PixKey[]; raw: any }>(`${BASE}/pix/keys`, {}, 'get')
}

/**
 * Só chave aleatória (EVP): o provedor não cria chave de CPF, CNPJ, e-mail ou
 * telefone por API — elas exigem confirmação de posse junto ao Banco Central e
 * são cadastradas pelo titular no aplicativo da conta digital.
 */
export async function createPixKey(password: string): Promise<ApiResponse<PixKey>> {
    return apiRequest<PixKey>(`${BASE}/pix/keys`, { data: { type: 'EVP', password } }, 'post')
}

export async function deletePixKey(id: string, password: string): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`${BASE}/pix/keys/${id}`, { data: { password } }, 'delete')
}

// ============ PIX QR ============
export interface PixStaticQr {
    id: string
    encodedImage?: string
    payload?: string
    addressKey?: string
    description?: string
    value?: number
}

// Sem listagem de QR estáticos: o provedor só oferece criar, apagar e
// decodificar. Histórico exigiria persistir o QR no ato da criação.

export async function createStaticPixQr(data: {
    addressKey: string
    description?: string
    value?: number
    expirationDate?: string
    allowsMultiplePayments?: boolean
}): Promise<ApiResponse<PixStaticQr>> {
    return apiRequest<PixStaticQr>(`${BASE}/pix/qr-static`, { data }, 'post')
}

export async function sendPixQrByEmail(data: {
    email: string
    encodedImage: string
    payload: string
    valueCents?: number | null
    description?: string | null
}): Promise<ApiResponse<{ success: true }>> {
    return apiRequest<{ success: true }>(
        `${BASE}/pix/qr-static/send-email`,
        { data },
        'post',
    )
}

/** Resposta do provedor ao decodificar um Pix copia e cola (valores em reais). */
export interface DecodedPix {
    payload: string
    type: 'STATIC' | 'DYNAMIC' | 'DYNAMIC_WITH_ASAAS_ADDRESS_KEY' | 'COMPOSITE'
    pixKey?: string | null
    dueDate?: string | null
    expirationDate?: string | null
    value?: number | null
    interest?: number | null
    fine?: number | null
    discount?: number | null
    totalValue?: number | null
    canBePaidWithDifferentValue?: boolean
    canBePaid?: boolean
    cannotBePaidReason?: string | null
    description?: string | null
    receiver?: {
        ispbName?: string | null
        name?: string | null
        tradingName?: string | null
        cpfCnpj?: string | null
    } | null
}

export async function decodePix(payload: string): Promise<ApiResponse<DecodedPix>> {
    return apiRequest<DecodedPix>(`${BASE}/pix/decode`, { data: { payload } }, 'post')
}

/** Paga o QR (estático ou dinâmico) em vez de transferir para a chave contida nele. */
export async function payPixQrCode(data: {
    payload: string
    valueCents: number
    description?: string
    scheduleDate?: string
    password: string
}): Promise<ApiResponse<{ id: string; status: string }>> {
    return apiRequest<{ id: string; status: string }>(`${BASE}/pix/qr-code/pay`, { data }, 'post')
}

export async function sendPix(data: {
    pixKey: string
    pixKeyType: string
    amountCents: number
    description?: string
    password: string
}): Promise<ApiResponse<{ providerTransferId: string; status: string }>> {
    return apiRequest<{ providerTransferId: string; status: string }>(
        `${BASE}/pix/send`,
        { data },
        'post',
    )
}

// ============ TRANSFERENCIAS ============
export interface TransferItem {
    id: string
    value: number
    operationType: string
    status: string
    description: string | null
    transferDate: string
    bankAccount?: any
    pixAddressKey?: string
}

export async function listTransfers(params?: {
    offset?: number
    limit?: number
}): Promise<ApiResponse<{ items: TransferItem[]; total: number; hasMore: boolean; raw: any }>> {
    return apiRequest<{ items: TransferItem[]; total: number; hasMore: boolean; raw: any }>(
        `${BASE}/transfers`,
        { params: params || {} },
        'get',
    )
}

export async function sendTed(data: {
    bankCode: string
    agency: string
    accountNumber: string
    accountDigit?: string
    holderName: string
    holderCpfCnpj: string
    amountCents: number
    description?: string
    password: string
}): Promise<ApiResponse<{ providerTransferId: string; status: string }>> {
    return apiRequest<{ providerTransferId: string; status: string }>(
        `${BASE}/transfers/ted`,
        { data },
        'post',
    )
}

// ============ RECARGAS ============
export interface MobileRecharge {
    id: string
    phoneNumber: string
    value: number
    operator: string
    status: string
    rechargeDate: string
}

export async function listMobileRecharges(params?: {
    offset?: number
    limit?: number
}): Promise<ApiResponse<{ items: MobileRecharge[]; total: number; hasMore: boolean; raw: any }>> {
    return apiRequest<{ items: MobileRecharge[]; total: number; hasMore: boolean; raw: any }>(
        `${BASE}/recharges`,
        { params: params || {} },
        'get',
    )
}

export async function createMobileRecharge(data: {
    phoneNumber: string
    value: number
    operator: string
    password: string
}): Promise<ApiResponse<MobileRecharge>> {
    return apiRequest<MobileRecharge>(`${BASE}/recharges`, { data }, 'post')
}

// ============ PAGAMENTO DE CONTAS ============
export interface BillPayment {
    id: string
    value: number
    status: string
    description: string | null
    paymentDate: string
    dueDate?: string
}

export async function listBillPayments(params?: {
    offset?: number
    limit?: number
}): Promise<ApiResponse<{ items: BillPayment[]; total: number; hasMore: boolean; raw: any }>> {
    return apiRequest<{ items: BillPayment[]; total: number; hasMore: boolean; raw: any }>(
        `${BASE}/bills`,
        { params: params || {} },
        'get',
    )
}

/** Resposta do provedor à simulação de pagamento de conta (valores em reais). */
export interface BillSimulation {
    minimumScheduleDate?: string | null
    fee?: number | null
    bankSlipInfo?: {
        identificationField?: string | null
        value?: number | null
        dueDate?: string | null
        companyName?: string | null
        beneficiaryName?: string | null
        beneficiaryCpfCnpj?: string | null
        allowChangeValue?: boolean
        minValue?: number | null
        maxValue?: number | null
        discountValue?: number | null
        interestValue?: number | null
        fineValue?: number | null
        isOverdue?: boolean
    } | null
}

export async function simulateBill(identificationField: string): Promise<ApiResponse<BillSimulation>> {
    return apiRequest<BillSimulation>(`${BASE}/bills/simulate`, { data: { identificationField } }, 'post')
}

export async function payBill(data: {
    identificationField: string
    value?: number
    scheduleDate?: string
    description?: string
    dueDate?: string
    password: string
}): Promise<ApiResponse<BillPayment>> {
    return apiRequest<BillPayment>(`${BASE}/bills`, { data }, 'post')
}

// ============ ANTECIPACOES ============
export interface Anticipation {
    id: string
    payment?: string
    installment?: string
    value: number
    netValue: number
    fee: number
    status: string
    requestDate: string
}

export async function listAnticipations(params?: {
    offset?: number
    limit?: number
    status?: string
}): Promise<ApiResponse<{ items: Anticipation[]; total: number; hasMore: boolean; raw: any }>> {
    return apiRequest<{ items: Anticipation[]; total: number; hasMore: boolean; raw: any }>(
        `${BASE}/anticipations`,
        { params: params || {} },
        'get',
    )
}

export async function createAnticipation(data: {
    payment?: string
    installment?: string
    password: string
}): Promise<ApiResponse<Anticipation>> {
    return apiRequest<Anticipation>(`${BASE}/anticipations`, { data }, 'post')
}

// ============ OPERADORES ============
export interface FinanceOperator {
    id: number
    name: string
    email: string
    userType: string
}

export async function getOperators(): Promise<
    ApiResponse<{ userIds: number[]; users: FinanceOperator[] }>
> {
    return apiRequest<{ userIds: number[]; users: FinanceOperator[] }>(
        `${BASE}/operators`,
        {},
    )
}

export async function getEligibleUsers(): Promise<ApiResponse<FinanceOperator[]>> {
    return apiRequest<FinanceOperator[]>(`${BASE}/operators/eligible`, {})
}

export async function isOperatorMe(): Promise<
    ApiResponse<{ isOperator: boolean; hasOperators: boolean }>
> {
    return apiRequest<{ isOperator: boolean; hasOperators: boolean }>(
        `${BASE}/operators/me`,
        {},
    )
}

export async function setOperators(
    userIds: number[],
    password: string,
): Promise<ApiResponse<{ userIds: number[] }>> {
    return apiRequest<{ userIds: number[] }>(
        `${BASE}/operators`,
        { data: { userIds, password } },
        'post',
    )
}
